import { differenceInBusinessDays as differenceInBusinessDaysDateFns } from "date-fns"
import { v4 as uuidv4 } from "uuid"

import { $t } from "@/plugins/i18n"
import {
  formatDate,
  formatDateAgo,
  getTodayCalendarDate,
  toActiveCalendarDate,
  toCalendarDate
} from "@/utils/dateUtil"
import {
  convertMsToTime,
  replaceHtmlTagsForEditor,
  replaceHtmlTagsForEditorWithoutTrim
} from "@/utils/helpers"
import { syncDescriptionHtml } from "@/utils/syncDescriptionHtml"

import { BacklogSetup } from "./Backlog"
import { Base } from "./Base"
import { Comment } from "./Comment"
import { NameIdAndUrl, ResourceType } from "./common"
import { DefinitionOfDone } from "./DefinitionOfDone"
import { StructuredDescription } from "./EditorData"
import { Epic } from "./Epic"
import { FileMetaData, IFile } from "./File"
import { ItemQueryPriorityType } from "./Filter/FilterBacklog"
import { PokerEstimation } from "./Poker"
import { ReferencedResourcePreviews, SearchProjectResult } from "./Search"
import { ServerResponse } from "./ServerResponse"
import { Sprint } from "./Sprint"
import { Status } from "./Status"
import { listOfSortedTaskTypes, Task, TaskType } from "./Task"
import { Team, TeamType } from "./Team"
import { ToDo } from "./ToDo"
import { Type, typeCreateById } from "./Type"
import { Picture, User } from "./User"

export const listOfStatusesWithMessage: ItemStatus[] = [
  "BLOCKED",
  "OBSOLETE",
  "PM_REVIEW",
  "CLIENT_REVIEW"
]
export const ItemStatusesList = [
  "NOT_STARTED",
  "IN_OPS",
  "OPS_DONE",
  "IN_UI_UX",
  "UI_UX_DONE",
  "IN_DESIGN",
  "DESIGN_COMPLETED",
  "IN_DEVELOPMENT",
  "DEVELOPMENT_DONE",
  "IN_QA",
  "TESTING_COMPLETE",
  "SCHEDULED_FOR_DEPLOYMENT",
  "DEPLOYED_TO_STAGING",
  "DEPLOYED_TO_PRODUCTION",
  "DONE",
  "BLOCKED",
  "UNBLOCKED",
  "OBSOLETE",
  "RE_OPEN",
  "ALL",
  "CLIENT_REVIEW",
  "PM_REVIEW",
  "IN_PROGRESS",
  "IN_DISCOVERY",
  "DISCOVERY_DONE",
  "INFRASTRUCTURE",
  "INFRASTRUCTURE_DONE",
  "CI_CD_PIPELINE",
  "PIPELINE_DONE",
  "SECURITY",
  "SECURITY_DONE",
  "MITIGATION",
  "MITIGATION_DONE",
  "REMEDIATING",
  "REMEDIATION_DONE",
  "IN_TESTING",
  "TESTING_DONE",
  "IN_REVIEW",
  "REVIEW_DONE",
  "OBSERVABILITY",
  "OBSERVABILITY_DONE",
  "ALERTING",
  "ALERTING_DONE",
  "PERFORMANCE",
  "PERFORMANCE_DONE",
  "COST_OPTIMIZATION",
  "OPTIMIZATION_DONE",
  "DOCUMENTATION",
  "DOCUMENTATION_DONE",
  "IN_RESEARCH",
  "RESEARCH_DONE",
  "CONTENT_CREATING",
  "CONTENT_DONE",
  "IN_GRAPHIC_DESIGN",
  "GRAPHIC_DESIGN_DONE",
  "IN_SEO",
  "SEO_DONE",
  "IN_SMM",
  "SMM_DONE",
  "IN_UX",
  "UX_DONE",
  "TESTING",
  "UI_TESTING_DONE",
  "IN_UI_DESIGN",
  "UI_DESIGN_DONE",
  "DELIVERY",
  "DELIVERY_DONE",
  "DEV_HANDOFF",
  "HANDOFF_DONE"
]

export const itemSeverityLevelList = ["NONE", "TRIVIAL", "MEDIUM", "HIGH", "CRITICAL"]
export type ItemSeverityLevel = (typeof itemSeverityLevelList)[number]
export type ItemStatus = (typeof ItemStatusesList)[number]
export const storyStatusGroupList = [
  "ACTIVE",
  "NOT_COMPLETED",
  "IN_WORK",
  "DISPLAYING_LAST_STATUS_CHANGE_COMMENT",
  "NON_PROGRESS",
  "NON_PROGRESS_OR_RE_OPEN",
  "PM_AND_CLIENT_REVIEW",
  "TASK_BREAKDOWN_RETAINED"
] as const
export type StoryStatusGroup = (typeof storyStatusGroupList)[number]

export const reviewStatuses: ItemStatus[] = ["CLIENT_REVIEW", "PM_REVIEW"]

export const ItemTaskBreakdownStatuses: ItemStatus[] = ["NOT_STARTED", "BLOCKED", "RE_OPEN"]

/** Backend StoryStatus.NOT_ACTIVE */
export const ItemNotActiveStatuses: ItemStatus[] = [
  "NOT_STARTED",
  "RE_OPEN",
  "DONE",
  "OBSOLETE",
  "BLOCKED",
  "UNBLOCKED"
]

export type TeamChangeConfirmation = "UNASSIGN_WITH_NOTE" | "UNASSIGN" | "RETURN_WITH_TASK"

export const itemTypesList = [
  "STORY",
  "SPIKE",
  "BUG",
  "SUPPORT_CASE",
  "TECH_DEBT",
  "DESIGN_DEBT",
  "DEV_OPS",
  "DEV",
  "OPS",
  "INCIDENT"
] as const

export type ItemType = (typeof itemTypesList)[number] | "TASK"

export const ItemStatusesWithPriority: ItemStatus[] = [
  "OBSOLETE",
  "RE_OPEN",
  "BLOCKED",
  "UNBLOCKED",
  "NOT_STARTED",
  "IN_OPS",
  "OPS_DONE",
  "IN_DISCOVERY",
  "DISCOVERY_DONE",
  "IN_UX",
  "UX_DONE",
  "IN_UI_DESIGN",
  "UI_DESIGN_DONE",
  "TESTING",
  "UI_TESTING_DONE",
  "DELIVERY",
  "DELIVERY_DONE",
  "DEV_HANDOFF",
  "HANDOFF_DONE",
  "INFRASTRUCTURE",
  "INFRASTRUCTURE_DONE",
  "CI_CD_PIPELINE",
  "PIPELINE_DONE",
  "IN_RESEARCH",
  "RESEARCH_DONE",
  "CONTENT_CREATING",
  "CONTENT_DONE",
  "IN_GRAPHIC_DESIGN",
  "GRAPHIC_DESIGN_DONE",
  "IN_UI_UX",
  "UI_UX_DONE",
  "IN_SEO",
  "SEO_DONE",
  "IN_SMM",
  "SMM_DONE",
  "IN_DESIGN",
  "DESIGN_COMPLETED",
  "IN_DEVELOPMENT",
  "DEVELOPMENT_DONE",
  "SECURITY",
  "SECURITY_DONE",
  "MITIGATION",
  "MITIGATION_DONE",
  "REMEDIATING",
  "REMEDIATION_DONE",
  "IN_TESTING",
  "TESTING_DONE",
  "IN_REVIEW",
  "REVIEW_DONE",
  "OBSERVABILITY",
  "OBSERVABILITY_DONE",
  "ALERTING",
  "ALERTING_DONE",
  "PERFORMANCE",
  "PERFORMANCE_DONE",
  "COST_OPTIMIZATION",
  "OPTIMIZATION_DONE",
  "IN_QA",
  "TESTING_COMPLETE",
  "DOCUMENTATION",
  "DOCUMENTATION_DONE",
  "CLIENT_REVIEW",
  "PM_REVIEW",
  "CLIENT_REVIEW",
  "DEPLOYED_TO_STAGING",
  "DEPLOYED_TO_PRODUCTION",
  "DONE"
]

export const ItemNotInProgressStatuses: ItemStatus[] = [
  "OBSOLETE",
  "DONE",
  "BLOCKED",
  "UNBLOCKED",
  "NOT_STARTED"
]

export class ItemConfig {
  itemTypes: ItemType[] = []
  taskTypes: TaskType[] = []
  defaultTaskType: TaskType = "DEV"
  definitionsOfDone: DefinitionOfDone[] = []
  labels: ItemConfigLabel[] = []

  constructor(props?: Partial<ItemConfig>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.definitionsOfDone = this.definitionsOfDone.map(d => new DefinitionOfDone(d))
    this.labels = this.labels.map(label => new ItemConfigLabel(label))
  }
}

export class ItemConfigLabel {
  label = ""
  analyticsGroup = ""

  constructor(props?: Partial<ItemConfigLabel>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class ItemValidateCheckPayload {
  itemType?: ItemType
  teamType?: TeamType
  teamId?: string

  constructor(props?: Partial<ItemValidateCheckPayload>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class ItemValidateCheckPayloadCreate {
  itemType?: ItemType
  teamType?: TeamType
  targetTaskTypes: TaskType[] = []
  targetTeamType: TeamType = "DEVELOPMENT"
  targetItemType: ItemType = "STORY"

  constructor(props?: Partial<ItemValidateCheckPayloadCreate>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export const itemChangeValidationResultCodes = [
  "OK",
  "TEAM_DOES_NOT_SUPPORT_ITEM_TYPE",
  "TEAM_TYPE_DOES_NOT_SUPPORT_ITEM_TYPE",
  "TASK_TYPES_INCOMPATIBLE",
  "ITEM_ALREADY_STARTED",
  "ITEM_TYPE_NOT_SUPPORTED_BY_TEAM_TYPE"
] as const

export type ItemChangeValidationResultCode = (typeof itemChangeValidationResultCodes)[number]

export class ItemChangeTooltipValidation {
  tooltipCode: string = ""
  message = ""

  constructor(props?: Partial<ItemChangeTooltipValidation>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class ItemChangeValidationResultDto {
  code: ItemChangeValidationResultCode = "OK"
  incompatibleTaskTypes: TaskType[] = []
  message = ""

  constructor(props?: Partial<ItemChangeValidationResultDto>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class AvailableItemConfig {
  availableItemTypes: ItemType[] = []
  configs: ItemConfig[] = []

  constructor(props?: Partial<AvailableItemConfig>) {
    if (!props) return
    Object.keys(props).forEach(key => {
      this[key] = props[key]
    })
    this.configs = this.configs.map(c => new ItemConfig(c))
  }
}

export class ItemConfigs {
  DEVELOPMENT: AvailableItemConfig = new AvailableItemConfig()
  UI_UX: AvailableItemConfig = new AvailableItemConfig()
  DEVOPS: AvailableItemConfig = new AvailableItemConfig()
  MARKETING: AvailableItemConfig = new AvailableItemConfig()
  NONE: AvailableItemConfig = new AvailableItemConfig()

  constructor(props?: Partial<ItemConfigs>) {
    if (!props) return
    this.DEVELOPMENT = new AvailableItemConfig(props?.DEVELOPMENT)
    this.UI_UX = new AvailableItemConfig(props?.UI_UX)
    this.DEVOPS = new AvailableItemConfig(props?.DEVOPS)
    this.MARKETING = new AvailableItemConfig(props?.MARKETING)
    this.NONE = new AvailableItemConfig(props?.NONE)
  }
}

export interface ServerSendCreatedItem {
  item: Item
  type: ResourceType
}

export interface ItemFile {
  itemId: string
  file: File
}

export interface ChangeTeamForItem {
  team: Team | null
  item: Item
  setUnprioritizePriority?: boolean
  /** Replaces the backend's canned comment when the Item gets blocked */
  message?: StructuredDescription
}

export interface ChangeTeamForItems {
  team: Team
  items: Item[]
  moveTo?: string
}

export interface SeverityLevelExtend {
  id: ItemSeverityLevel
  label: string
}

export class TasksWithItemStatus {
  tasks: Task[] = []
  status: ItemStatus = "NOT_STARTED"
  estimation = 0
  sequenceNumber: number
  constructor(props: TasksWithItemStatus) {
    this.tasks = props.tasks.map(t => Task.create(t))
    this.status = props.status
    this.estimation = props.estimation
    this.sequenceNumber = props.sequenceNumber
  }
}

export interface DefinitionWithItemStatus {
  definitionsOfDone: DefinitionOfDone[]
  acceptanceCriteria?: StructuredDescription
  status: ItemStatus
  readyToComplete: boolean
  estimation: number
}

export interface ChangeItemStatus {
  projectId?: string
  itemId: string
  status: string
  oldStatus: ItemStatus
  message?: StructuredDescription
  itemTitle?: string
  todoItem?: ToDo
  fileMetadataIds?: string[]
}

export interface ItemPreviewDto {
  id: string
  itemType: ItemType
  label?: string
  projectId: string
  sequenceNumber: number
  status: ItemStatus
  title: string
}

export interface ChangeItemOwner {
  itemId: string
  ownerId: string
  projectId: string
}

export interface ItemDefinitionPyload {
  itemId: string
  definitionOfDoneId: string
  projectId?: string
}

export interface AddItemEpicPayload {
  itemId: string[]
  epicId: string
}

export interface EmptyStory {
  title: string
  epic: Epic | null
  team: Team | null
  type: ItemType
  priority: ItemPriority | null
  sprintId: string
  sprintIsActive: boolean
  sprintSequenceNumber: number | null
  addToBacklog?: string
  moveFrom?: string
  sprintIsContinuous?: boolean
  sprint?: Sprint
}

export interface ItemProgress {
  projectId?: string
  itemId: string
  tasks: Task[]
  definitionsOfDone?: DefinitionOfDone[]
}

export type ItemOptions = {
  id: string
  label: string
}

export type ChangeItemByUser = {
  id: string
  firstName: string
  lastName: string
  picture: Picture
  email?: string | null
}

export type CreationSource = "JIRA" | null

export interface ExternalResource {
  id: string
  importId: string
  url: string
}

export interface ItemPriority {
  priorityType: string
  previousItemId: string | null
  nextItemId: string | null
  index?: number
}

export interface ItemPriorityParams {
  backlogPriority: number
  epicPriority: number
  sprintPriority: number
  sprint: Sprint
  unprioritizedOrder: number
}

export interface StatusChangeInitiator {
  initiator: ChangeItemByUser
  status: ItemStatus
  description: StructuredDescription
}

export interface FeContext {
  changeId: string
}

export interface IItem {
  title: string
  itemType: ItemType
  status?: ItemStatus
  priority?: ItemPriority | null
  severityLevel?: ItemSeverityLevel
  description?: string
  structuredDescription: StructuredDescription
  storyPoints?: number
  id?: string
  definitionsOfDone?: DefinitionOfDone[]
  tasks?: Task[]
  taskOwners?: User[]
  owner?: User | null
  fileMetadata?: IFile[]
  attachmentIds?: string[]
  epic?: Epic | null
  team?: Team
  createdDate: string
  modifiedDate?: string
  modifiedByUser?: ChangeItemByUser | null
  createdByUser?: ChangeItemByUser | null
  backlogPriority: number | null
  unprioritizedOrder?: number
  sequenceNumber: number
  estimation?: number
  originalEstimation?: number
  storyPokerId?: string
  sprint?: Sprint
  sprintId: string
  sprintSequenceNumber: number | null
  referencedResourcePreviews?: ReferencedResourcePreviews
  addToBacklog?: string
  moveFrom?: string
  hasPostMortem: boolean
  label: string
  feContext: FeContext
  expectedDeliveryDate?: string
  convertedToEpic?: boolean
  hasDemoVideo?: boolean
  creationSource?: CreationSource
}

export class Item extends Base<Item> {
  title: string
  itemType: ItemType
  itemTypeParsed: Type
  initiator: ChangeItemByUser | null
  status: ItemStatus
  statusParsed?: Status
  priority?: ItemPriority | null
  severityLevel?: ItemSeverityLevel
  severityLevelParsed?: { id: ItemSeverityLevel; label: string }
  description?: string
  structuredDescription: StructuredDescription
  acceptanceCriteria?: StructuredDescription
  spec?: StructuredDescription
  storyPoints?: number | null
  id?: string
  definitionsOfDone: DefinitionOfDone[]
  tasks: Task[]
  taskOwners?: User[]
  owner?: User | null
  fileMetadata?: FileMetaData[]
  deletedFileMetadata?: FileMetaData[]
  attachmentIds?: string[]
  epic?: Epic | null
  team: Team | null
  /** Returning a Done Item to this team reopens it */
  lastRemovedTeamId?: string | null
  teamType?: TeamType
  projectId?: string
  project?: SearchProjectResult
  createdDate: string
  modifiedDate?: string
  modifiedByUser?: ChangeItemByUser | null
  createdByUser?: ChangeItemByUser | null
  backlogPriority: number | null
  sprintPriority?: number | null
  epicPriority?: number | null
  unprioritizedOrder?: number
  sequenceNumber: number
  estimation?: number
  originalEstimation?: number
  milestoneId?: string
  changedFieldsOriginValues: { [key: string]: any }
  storyPokerId?: string
  sprint?: Sprint
  sprintId?: string
  sprintIsActive?: boolean
  sprintIsContinuous?: boolean
  referencedResourcePreviews?: ReferencedResourcePreviews
  sprintSequenceNumber: number | null
  addToBacklog?: string
  moveFrom?: string
  activeStoryPoker: boolean
  hasPostMortem: boolean
  label: string
  backlogSetup?: BacklogSetup
  isInline?: boolean
  ready: boolean
  lastStatusChangeInSprint?: Date
  noteId?: string
  pokerEstimation?: PokerEstimation
  hasNotEstimatedTask: boolean
  statusChangeDescription: StructuredDescription
  statusChangeComment: Comment | null
  externalResourceId?: ExternalResource
  shouldHideAnalytics?: boolean | null
  croppedPreview?: boolean
  readOnly?: boolean
  isDeleted?: boolean
  feContext: FeContext
  convertedFromTask?: boolean
  expectedDeliveryDate?: string
  convertedToEpic?: boolean
  creationSource?: CreationSource
  hasDemoVideo: boolean

  constructor(props?: Partial<Item>) {
    super()
    this.setDefaultData()

    if (!props) return
    this.croppedPreview = props.croppedPreview ?? false
    this.readOnly = props.readOnly ?? false
    this.id = props.id ?? ""
    this.title = props.title ?? ""
    this.description = props.description ?? ""
    this.activeStoryPoker = props.activeStoryPoker ?? false

    this.structuredDescription = new StructuredDescription(props.structuredDescription)
    this.structuredDescription.html = syncDescriptionHtml({
      referencedResourcePreviews: props.referencedResourcePreviews,
      structuredDescription: this.structuredDescription
    })

    this.acceptanceCriteria = props.acceptanceCriteria
      ? new StructuredDescription(props.acceptanceCriteria)
      : undefined

    this.spec = props.spec ? new StructuredDescription(props.spec) : undefined

    this.itemType = props.itemType === "TASK" ? "STORY" : (props.itemType ?? "STORY")
    this.itemTypeParsed = typeCreateById({ id: this.itemType ?? "STORY" })

    this.storyPoints = props.storyPoints ?? null
    this.status = props.status ?? "NOT_STARTED"
    this.statusParsed = Status.createById({
      id: props.status ?? "NOT_STARTED"
    })

    this.priority = props.priority ?? null
    this.severityLevel = props.severityLevel ?? "HIGH"

    this.severityLevelParsed = this.getParsedSeverityLevel(this.severityLevel)

    this.tasks = props.tasks?.length ? props.tasks.map(t => Task.create(t)) : []
    this.taskOwners = props.taskOwners?.length
      ? props.taskOwners.filter(t => t !== null).map(t => User.create(t))
      : Item.getTaskOwners(this.tasks)
    this.definitionsOfDone = props.definitionsOfDone
      ? props.definitionsOfDone.map(d => DefinitionOfDone.create(d))
      : []
    this.fileMetadata = props.fileMetadata
      ? props.fileMetadata.map(d => FileMetaData.create(d))
      : []
    this.deletedFileMetadata = props.deletedFileMetadata
      ? props.deletedFileMetadata.map(d => FileMetaData.create(d))
      : []
    this.attachmentIds = props.fileMetadata?.length ? props.fileMetadata.map(f => f.id) : []
    this.owner = props.owner ? User.create(props?.owner) : null
    this.epic = props.epic ?? null
    this.team = props.team ? Team.create(props.team) : null
    this.createdDate = props.createdDate ?? ""
    this.modifiedDate = props.modifiedDate ?? ""
    this.modifiedByUser = props.modifiedByUser ?? null
    this.createdByUser = props.createdByUser ?? null
    this.backlogPriority = props.backlogPriority ?? null
    this.sprintPriority = props.sprintPriority ?? null
    this.epicPriority = props.epicPriority ?? null
    this.unprioritizedOrder = props.unprioritizedOrder ?? 0
    this.sequenceNumber = props.sequenceNumber ?? 1
    this.originalEstimation = props.originalEstimation ?? 0
    this.projectId = props.projectId ?? ""
    this.project = props.project ? new SearchProjectResult(props.project) : undefined
    this.storyPokerId = props.storyPokerId ?? ""
    this.sprint = props.sprint ? Sprint.create(props.sprint) : undefined
    this.sprintId = props.sprintId ?? this.sprint?.id ?? ""
    this.sprintIsActive = props.sprintIsActive ?? false
    this.sprintIsContinuous = props.sprintIsContinuous ?? false
    this.sprintSequenceNumber = props.sprintSequenceNumber ?? null
    this.addToBacklog = props.addToBacklog ?? undefined
    this.moveFrom = props.moveFrom ?? undefined
    this.hasPostMortem = props.hasPostMortem ?? false
    this.label = props.label ?? ""
    this.backlogSetup = props.backlogSetup ?? undefined
    this.isInline = props.isInline ?? false
    this.ready = props.ready ?? true
    this.estimation = props.estimation ?? undefined
    this.noteId = props.noteId ?? undefined
    this.hasNotEstimatedTask = props.hasNotEstimatedTask ?? false

    this.lastStatusChangeInSprint = props.lastStatusChangeInSprint
      ? new Date(props.lastStatusChangeInSprint)
      : undefined
    this.pokerEstimation = props.pokerEstimation ?? undefined

    this.statusChangeDescription = new StructuredDescription(props.statusChangeDescription)

    this.statusChangeComment = props.statusChangeComment
      ? Comment.create(props.statusChangeComment)
      : null
    this.initiator = props.initiator ?? null

    this.externalResourceId = props.externalResourceId ?? undefined
    this.shouldHideAnalytics = props.shouldHideAnalytics ?? false
    this.isDeleted = props.isDeleted ?? false
    this.feContext = props?.feContext ?? { changeId: uuidv4() }
    this.teamType = props.teamType ?? this.team?.type
    this.lastRemovedTeamId = props.lastRemovedTeamId ?? null
    this.convertedFromTask = props.convertedFromTask ?? false
    this.expectedDeliveryDate = props.expectedDeliveryDate ?? undefined
    this.convertedToEpic = props.convertedToEpic ?? false
    this.creationSource = props.creationSource ?? null
    this.hasDemoVideo = props.hasDemoVideo ?? false
  }

  setDefaultData() {
    this.id = ""
    this.title = ""
    this.description = ""
    this.structuredDescription = new StructuredDescription()
    this.acceptanceCriteria = undefined
    this.spec = undefined
    this.itemType = "STORY"
    this.itemTypeParsed = typeCreateById({ id: "STORY" })

    this.storyPoints = null
    this.status = "NOT_STARTED"
    this.statusParsed = Status.createById({ id: "NOT_STARTED" })

    this.priority = null
    this.severityLevel = "HIGH"
    this.severityLevelParsed = this.getParsedSeverityLevel("HIGH")

    this.tasks = []
    this.team = null
    this.taskOwners = []
    this.definitionsOfDone = []
    this.fileMetadata = []
    this.deletedFileMetadata = []
    this.attachmentIds = []
    this.createdDate = ""
    this.sequenceNumber = 1
    this.storyPokerId = ""
    this.changedFieldsOriginValues = {}
    this.sprint = undefined
    this.sprintId = ""
    this.sprintIsActive = false
    this.sprintIsContinuous = false
    this.sprintSequenceNumber = null
    this.activeStoryPoker = false
    this.hasPostMortem = false
    this.label = ""
    this.ready = true
    this.noteId = ""
    this.statusChangeDescription = new StructuredDescription()
    this.backlogPriority = null
    this.epicPriority = null
    this.expectedDeliveryDate = undefined
    this.convertedToEpic = false
    this.hasDemoVideo = false
  }

  hasChangedProp(prop) {
    return Object.keys(this.changedFieldsOriginValues).includes(prop)
  }

  oldValueForProp(prop) {
    return this.changedFieldsOriginValues[prop]
  }

  onValueUpdate(payload) {
    if (payload["id"] === "fileMetadata") return

    if (!this.hasChangedProp(payload["id"])) {
      this.changedFieldsOriginValues[payload["id"]] = this[payload["id"]]
    }
    if (payload["id"] === "team") this.cleanOwners(payload["value"])
  }

  cleanOwners(team) {
    if (!team?.users) {
      return
    }
    const acceptableIds = team.users.map(u => u.id)

    if (this.owner && !acceptableIds.includes(this.owner.id)) {
      this.owner = null
    }

    this.tasks.forEach(task => {
      if (task.owner && !acceptableIds.includes(task.owner.id)) {
        task.owner = undefined
      }
    })
  }

  get statusChangeInitiator(): ChangeItemByUser {
    return (
      this.initiator || {
        id: "",
        firstName: this.statusChangeComment?.owner?.firstName ?? "",
        lastName: this.statusChangeComment?.owner?.lastName ?? "",
        picture: { pictureUrl: null }
      }
    )
  }

  get isEstimated() {
    const estimation = this.estimation
    if (estimation === undefined) return false
    return (this.isSupportCase || this.storyPoints !== null) && estimation > 0
  }

  get isEstimatedWithoutStoryPoints() {
    return this.estimation !== undefined && this.estimation > 0
  }

  get hasExpectedDeliveryDate() {
    return !!this.expectedDeliveryDate
  }

  get parsedExpectedDeliveryDate() {
    return this.expectedDeliveryDate ? toCalendarDate(this.expectedDeliveryDate) : null
  }

  get formattedExpectedDeliveryDate() {
    return this.formatExpectedDeliveryDate()
  }

  get isExpectedDeliveryDateOverdue() {
    if (!this.parsedExpectedDeliveryDate) return false
    return this.parsedExpectedDeliveryDate < getTodayCalendarDate()
  }

  formatExpectedDeliveryDate(dateFormat = "dd MMM yyyy", emptyValue = "") {
    if (!this.expectedDeliveryDate) return emptyValue
    return formatDate(this.expectedDeliveryDate, dateFormat, { dateOnly: true })
  }

  get isNotStarted(): boolean {
    return this.status === "NOT_STARTED"
  }

  get withoutEstimation() {
    return this.estimation === undefined || this.estimation === null
  }

  get canAddTeam() {
    return this.itemTypeParsed?.id !== "SUPPORT_CASE"
  }

  get allUsersInItem() {
    const users: (User | undefined | null)[] = []

    if (this.owner) {
      users.push(this.owner)
    } else {
      users.push(null)
    }

    this.taskOwners?.forEach(item => {
      if (!item?.id) return
      const result = users.find(u => u?.id === item.id)
      if (!result) {
        users.push(item)
      }
    })
    return users
  }

  static createDefault(): Item {
    return new Item()
  }

  getJsonObj() {
    return {
      title: this.title,
      structuredDescription: new StructuredDescription(this.structuredDescription).getJsonObj(),
      ...(this.acceptanceCriteria
        ? { acceptanceCriteria: new StructuredDescription(this.acceptanceCriteria).getJsonObj() }
        : {}),
      ...(this.spec ? { spec: new StructuredDescription(this.spec).getJsonObj() } : {}),
      itemType: this.itemTypeParsed ? this.itemTypeParsed.id : "",
      storyPoints: this.storyPoints,
      status: this.statusParsed ? this.statusParsed.id : "",
      priority: this.priority,
      severityLevel:
        this.showSeverity && this.severityLevelParsed ? this.severityLevelParsed.id : "NONE",
      owner: this.owner,
      ownerId: this.owner && this.owner?.id ? this.owner?.id : "",
      definitionsOfDone: this.definitionsOfDone?.map(d => DefinitionOfDone.create(d).getJsonObj()),
      tasks: this.tasks?.filter(task => task.isNotEmpty).map(t => t.getJsonObj()),
      attachmentIds: this.attachmentIds?.filter(id =>
        this.tasks.every(task => task.fileMetadataIds?.every(fileId => fileId !== id))
      ),
      epicId: this.epic?.id ?? null,
      teamId: this.team?.id ?? null,
      createdDate: this.createdDate,
      sequenceNumber: this.sequenceNumber,
      projectId: this.projectId,
      sprint: this.sprint,
      sprintId: this.sprintId,
      label: this.label,
      noteId: this.noteId,
      feContext: this.feContext,
      teamType: this.teamType ?? this.team?.type
    }
  }

  static create(props: Item): Item {
    return new Item(props)
  }

  static getTaskOwners(tasks: Task[]): User[] {
    return tasks.filter(t => t.owner !== null).map(t => User.create(t.owner))
  }

  static createEmptyStory(payload: EmptyStory): Item {
    const item = new Item()
    item.title = payload.title
    item.epic = payload.epic
    item.team = payload.team
    item.itemTypeParsed = typeCreateById({ id: payload.type ?? "STORY" })
    item.itemType = payload.type
    item.severityLevel = "NONE"
    item.definitionsOfDone = []
    item.priority = payload.priority
    item.sprintId = payload.sprintId
    item.sprintIsActive = payload.sprintIsActive
    item.sprintIsContinuous = payload.sprintIsContinuous
    item.sprintSequenceNumber = payload.sprintSequenceNumber
    item.addToBacklog = payload.addToBacklog
    item.moveFrom = payload.moveFrom
    item.sprint = payload.sprint
    return item
  }

  static get severityLevelsParsed() {
    return severityLevelsParsed
  }

  get showSeverity() {
    return (
      this.itemTypeParsed.id === "BUG" ||
      this.itemTypeParsed.id === "SUPPORT_CASE" ||
      this.itemTypeParsed.id === "INCIDENT"
    )
  }

  get notDone() {
    return this.status !== "DONE" && this.status !== "OBSOLETE"
  }

  get isShowTaskBreakDown() {
    return ItemTaskBreakdownStatuses.includes(this.status)
  }

  get isDone() {
    return this.statusParsed?.id === "DONE" || this.status === "DONE"
  }

  get isObsolete() {
    return this.statusParsed?.id === "OBSOLETE" || this.status === "OBSOLETE"
  }

  get isCompleted() {
    return this.isDone || this.isObsolete
  }

  get isBlocked() {
    return this.statusParsed?.id === "BLOCKED" || this.status === "BLOCKED"
  }

  /**
   * Mirrors the backend team change rules: only active work leaving an active Sprint is blocked, so
   * only it can keep a note; a Done Item returned to its last team is reopened, so it needs a task.
   */
  getTeamChangeConfirmation(team: Team | null): TeamChangeConfirmation | null {
    if (!team) {
      if (!this.team?.id) return null
      const inActiveSprint = this.sprint?.status === "STARTED"
      if (inActiveSprint && !ItemNotActiveStatuses.includes(this.status))
        return "UNASSIGN_WITH_NOTE"
      return inActiveSprint || !this.epic?.id ? "UNASSIGN" : null
    }
    return !this.team?.id && this.isDone && this.lastRemovedTeamId === team.id
      ? "RETURN_WITH_TASK"
      : null
  }

  get unsaveFields() {
    return [
      "title",
      "structuredDescription",
      "acceptanceCriteria",
      "spec",
      "pokerEstimation"
    ] as (keyof Item)[]
  }

  itemTypeWasChanged({ oldType, newType }) {
    const ignoreTypes = ["STORY", "SPIKE"]
    if (ignoreTypes.includes(oldType) && ignoreTypes.includes(newType)) return false
    return oldType !== newType
  }

  getParsedSeverityLevel(type) {
    const level = Item.severityLevelsParsed.find(item => item.id === type)

    return level ? level : Item.severityLevelsParsed[0]
  }

  getCheckUnsaveField(key: keyof Item) {
    return this.unsaveFields.includes(key)
  }

  /** Updates one persisted field in the dirty-state snapshot without accepting other edits. */
  markFieldAsSaved(key: keyof Item) {
    if (!this.initialState) {
      this.setInitialState()
      return
    }

    const initial = JSON.parse(this.initialState)
    initial[key] = this[key]
    this.initialState = JSON.stringify(initial)
  }

  getAutoSaveFields(paramId) {
    const idsList = ["storyPoints", "severityLevelParsed", "epic", "label", "itemTypeParsed"]
    return idsList.includes(paramId)
  }

  getUnfinishedTasksCount(type) {
    return this.tasks
      ?.filter(e => e.isNotEmpty)
      .filter(e => e.typeParsed.id === type)
      ?.filter(e => e.estimatedHours! > 0)?.length
  }

  isRemovable(): boolean {
    return (
      this.itemType === "STORY" &&
      this.status === "NOT_STARTED" &&
      !this.structuredDescription.html &&
      !this.storyPoints &&
      !this.owner &&
      !this.tasks?.length &&
      !this.fileMetadata?.length
    )
  }

  getTasksByUserId(userId) {
    return this.tasks?.filter(el => el.owner?.id === userId) ?? []
  }

  checkAlertTheTaskForwardedToUser(userId, taskId) {
    if (!this.tasks?.length) return false
    const taskIndex = this.tasks.findIndex(el => el.id === taskId)
    if (taskIndex <= 0) return false
    const previousTask = this.tasks[taskIndex - 1]
    if (previousTask.estimatedHours === 0 && previousTask.owner?.id !== userId) return true
  }

  isModified(keys?: (keyof Item)[], inverse?: boolean) {
    let isModified = false
    if (keys?.includes("structuredDescription") && this.initialState) {
      const initial = JSON.parse(this.initialState)
      if (initial.structuredDescription) {
        isModified =
          replaceHtmlTagsForEditorWithoutTrim(this.structuredDescription?.html) !==
          replaceHtmlTagsForEditorWithoutTrim(initial.structuredDescription?.html)
      }
      keys = keys.filter(key => key !== "structuredDescription")
    }
    if (keys?.includes("acceptanceCriteria") && this.initialState) {
      const initial = JSON.parse(this.initialState)
      isModified =
        isModified ||
        replaceHtmlTagsForEditorWithoutTrim(this.acceptanceCriteria?.html ?? "") !==
          replaceHtmlTagsForEditorWithoutTrim(initial.acceptanceCriteria?.html ?? "")
      keys = keys.filter(key => key !== "acceptanceCriteria")
    }
    if (keys?.includes("spec") && this.initialState) {
      const initial = JSON.parse(this.initialState)
      isModified =
        isModified ||
        replaceHtmlTagsForEditorWithoutTrim(this.spec?.html ?? "") !==
          replaceHtmlTagsForEditorWithoutTrim(initial.spec?.html ?? "")
      keys = keys.filter(key => key !== "spec")
    }
    if (!isModified) {
      isModified = super.isModified(keys, inverse)
    }
    return isModified
  }

  updateItemEstimationByTasks(tasks: Task[]) {
    return Task.getItemEstimation(tasks)
  }

  get estimationView() {
    if (this.withoutEstimation) return "-"
    if (this.estimation && this.estimation > 99) {
      return "99+"
    }
    return this.estimation
  }

  get daysSinceLastStatusChange() {
    if (!this.lastStatusChangeInSprint) return 0

    const differenceDays = differenceInBusinessDaysDateFns(
      getTodayCalendarDate(),
      toActiveCalendarDate(this.lastStatusChangeInSprint)
    )

    return differenceDays >= 0 ? differenceDays : 0
  }

  get isSupportCase() {
    return this.itemTypeParsed.id === "SUPPORT_CASE"
  }

  get isIncident() {
    return this.itemTypeParsed.id === "INCIDENT"
  }

  get isBug() {
    return this.itemTypeParsed.id === "BUG"
  }

  get inTheUnprioritized() {
    return this.backlogPriority === null && !this.inTheSprint
  }

  get inTheBacklog() {
    return this.backlogPriority !== null
  }
  get inTheSprint() {
    return this.sprintPriority !== null && this.sprint
  }

  get getPriorityType(): ItemQueryPriorityType {
    if (this.inTheSprint) return "SPRINT"
    if (this.inTheUnprioritized) return "UNPRIORITIZED"
    if (this.inTheBacklog) return "BACKLOG"

    return "BACKLOG"
  }

  get simpleDiff() {
    if (!this.id) return this
    return {
      title: this.title.trim(),
      structuredDescriptionHtml: replaceHtmlTagsForEditor(this.structuredDescription?.html)
    }
  }

  get canAddFiveWhys() {
    return this.isBug || this.isSupportCase || this.isIncident
  }

  static navigationByItem(item: Item) {
    if (!item.team?.id) {
      if (item.epic) {
        return {
          name: "epic",
          params: {
            epicId: item.epic.sequenceNumber ?? ""
          }
        }
      }
      return {
        name: "product-backlog"
      }
    }
    return {
      name: "backlog",
      params: {
        teamId: item.team.urlName
      },
      query: Item.getBacklogQueryByItem(item)
    }
  }

  static getBacklogTypeByItem(item: Item) {
    if (!item) return ""
    if (item?.backlogPriority === null && !item?.sprint) return "backlogUnprioritized"
    if (item?.sprint) return `backlogSprint`
    return $t(`backlog.backlogSetupIdByItemType['${item.itemTypeParsed.id.toLowerCase()}']`)
  }

  static getBacklogInstanceNameByItem(item: Item) {
    if (!item) return ""
    if (item?.backlogPriority === null && !item?.sprint) return "BacklogUnprioritized"
    if (item?.sprint) return `BacklogSprint#${item.sprint.sequenceNumber}`
    return $t(`backlog.backlogIdByItemType['${item.itemTypeParsed.id.toLowerCase()}']`)
  }

  static getBacklogSetupIdByItem(item: Item) {
    if (!item) return ""
    if (item?.backlogPriority === null && !item?.sprint) return "backlogUnprioritized"
    if (item?.sprint) return `backlogSprint_${item.sprint.sequenceNumber}`
    return $t(`backlog.backlogSetupIdByItemType['${item.itemTypeParsed.id.toLowerCase()}']`)
  }

  static getBacklogNameByItem(item: Item, fullName = false) {
    if (!item) return ""
    if (!item.team) {
      if (!item.epic || (item.status === "OBSOLETE" && item.sprint?.status !== "STARTED")) {
        return "Product Backlog"
      } else if (item.epic) {
        return `Epic #${item.epic.sequenceNumber}`
      }
    }

    if (item?.backlogPriority === null && !item?.sprint)
      return fullName ? "Unprioritized Backlog" : "Unprioritized"

    const backlogName = fullName
      ? $t(`backlog.titlesByItemTypeFull['${item.itemTypeParsed?.id?.toLowerCase()}']`)
      : $t(`backlog.titlesByItemType['${item.itemTypeParsed?.id?.toLowerCase()}']`)

    return item?.sprint ? `Sprint #${item.sprint?.sequenceNumber}` : backlogName
  }

  static getBacklogRouteByItem(item: Item) {
    if (!item.team && item.epic?.id) {
      return {
        name: "epic",
        params: {
          epicId: item.epic?.sequenceNumber ?? ""
        }
      }
    }

    if (!item.team || (item.status === "OBSOLETE" && item.sprint?.status !== "STARTED")) {
      return { name: "product-backlog" }
    }

    if (item.sprint?.status === "COMPLETED" || item.sprint?.status === "HALTED") {
      return {
        name: "SprintReview",
        params: {
          teamId: item.team.urlName,
          sprintId: item.sprint.sequenceNumber
        }
      }
    }

    return {
      name: "backlog",
      params: { teamId: item.team.urlName },
      query: Item.getBacklogQueryByItem(item)
    }
  }

  static getBacklogQueryByItem(item: Item) {
    const query = { b: "" }
    const b = item?.sprint
      ? `${$t("backlog.query.BacklogSprint")}#${item.sprint?.sequenceNumber}`
      : $t(`backlog.backlogQueryByItemType['${item.itemTypeParsed.id.toLowerCase()}']`)

    Object.assign(query, { b })
    // if item is unprioritized
    if (item?.backlogPriority === null && !item?.sprint) {
      Object.assign(query, {
        u: $t(`backlog.itemTypeToQueryParam['${item.itemTypeParsed.id}']`)
      })
    }

    return query
  }
}

const severityLevelsParsed: { id: ItemSeverityLevel; label: string }[] = [
  {
    id: "CRITICAL",
    label: "Critical"
  },
  {
    id: "HIGH",
    label: "High"
  },
  {
    id: "MEDIUM",
    label: "Medium"
  },
  {
    id: "TRIVIAL",
    label: "Trivial"
  }
]

export class ItemInSprintReview extends Item {
  sprintPriority: number | null = null
  sprintReviewComment: Comment | null = null
  users: User[] = []
  originalId = ""
  carriedForward = false
  movedToAnotherTeam: Team | null = null

  constructor(props) {
    super(props)

    this.sprintPriority = props.sprintPriority

    this.sprintReviewComment = this.statusChangeComment
      ? this.statusChangeComment
      : props.sprintReviewComment
        ? Comment.create(props.statusChangeComment)
        : null
    this.users = props.users.map(u => User.create(u))
    this.originalId = props.originalId
    this.carriedForward = props.carriedForward
    this.movedToAnotherTeam = props.movedToAnotherTeam
      ? Team.create(props.movedToAnotherTeam)
      : null
  }

  static create(props: Item): ItemInSprintReview {
    return new ItemInSprintReview(props)
  }
}

export class ItemInfo {
  id = ""
  sequenceNumber = 1
  title = ""
  itemType: ItemType = "STORY"
  itemTypeParsed: Type = typeCreateById({ id: "STORY" })
  status: ItemStatus = "NOT_STARTED"
  statusParsed: Status = Status.createById({ id: "NOT_STARTED" })

  constructor(props?: Partial<ItemInfo>) {
    this.id = props?.id ?? ""
    this.sequenceNumber = props?.sequenceNumber ?? 1
    this.title = props?.title ?? ""
    this.itemType = props?.itemType ?? "STORY"
    this.itemTypeParsed = typeCreateById({ id: this.itemType })
    this.status = props?.status ?? "NOT_STARTED"
    this.statusParsed = Status.createById({ id: this.status })
  }
}

export type MyWorkSectionId = "activeWork" | "workedOn" | "completed"

export class MyWork {
  projects: SearchProjectResult[] = []
  activeWork: Item[] = []
  workedOn: Item[] = []
  completed: Item[] = []
  stats: MyWorkStats = new MyWorkStats()

  constructor(props?: Partial<MyWork>) {
    if (!props) return
    this.projects = (props.projects ?? []).map(p => new SearchProjectResult(p))
    this.activeWork = (props.activeWork ?? []).map(i => new Item(i))
    this.workedOn = (props.workedOn ?? []).map(i => new Item(i))
    this.completed = (props.completed ?? []).map(i => new Item(i))
    this.stats = new MyWorkStats(props.stats)
  }
}

export class MyWorkSection {
  id: MyWorkSectionId = "activeWork"
  title = ""
  items: Item[] = []
  isLast = false

  constructor(props?: Partial<MyWorkSection>) {
    if (!props) return
    this.id = props.id ?? "activeWork"
    this.title = props.title ?? ""

    this.items = props.items ?? []
    this.isLast = props.isLast ?? false
  }
}

export class MyWorkStats {
  activeWork = 0
  completed = 0
  hours = 0
  tasks = 0
  total = 0
  workedOn = 0

  constructor(props?: Partial<MyWorkStats>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class ResponseToChangingItemTeam {
  id = ""
  title = ""
  itemType: ItemType = "STORY"
  itemTypeParsed = typeCreateById({ id: "STORY" })
  projectId = ""
  sequenceNumber = 1
  status: ItemStatus = "NOT_STARTED"
  statusParsed = Status.createById({ id: "NOT_STARTED" })
  team: Team | null = null
  sprint?: Sprint

  constructor(props?: Partial<ResponseToChangingItemTeam>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.itemTypeParsed = typeCreateById({ id: this.itemType })
    this.statusParsed = Status.createById({ id: this.status })
    this.team = this.team ? Team.create(this.team) : null
    this.sprint = this.sprint ? Sprint.create(this.sprint) : undefined
  }
}

export interface CycleTimeByTaskType {
  [key: string]: number
}

export class DiscoveredWork {
  bugAdded?: number
  bugAddedHours?: number
  discoveredWork?: number
  movedToItem?: number
  movedToItemHours?: number
  removedWork?: number
  taskAdded?: number
  taskAddedHours?: number
  taskDeleted?: number
  taskDeletedHours?: number
}

export class HistogramByStatusItem {
  duration = 0
  status: ItemStatus = "NOT_STARTED"
  sequentialDuration = 0
  teamChanged = false
  teamName = Team
  teamChangedOnNext = false

  constructor(props?: Partial<HistogramByStatusItem>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class ItemAnalytics {
  cycleTimeByTaskType: CycleTimeByTaskType = {}
  discoveredWork: DiscoveredWork = {}
  histogramByStatus: HistogramByStatusItem[] = []
  totalTaskCycleTime = 0
  startLeadTime = ""

  constructor(props?: Partial<ItemAnalytics>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.histogramByStatus = this.histogramByStatus.map(h => new HistogramByStatusItem(h))
    this.cycleTimeByTaskType = listOfSortedTaskTypes
      .filter(t => this.cycleTimeByTaskType[t] !== undefined)
      .reduce((acc, type) => {
        acc[type] = this.cycleTimeByTaskType[type] ?? 0
        return acc
      }, {})
  }

  get leadTimeAgo() {
    if (!this.startLeadTime) return `${$t("none")} days`
    return formatDateAgo({
      date: new Date(),
      dateFormat: new Date(this.startLeadTime),
      short: true
    })
  }

  get cycleTimeAgo() {
    if (!this.totalTaskCycleTime) return `${$t("none")} hr`
    const seconds = convertMsToTime(this.totalTaskCycleTime, "second")
    if (seconds < 60) {
      return `${Math.floor(seconds)} sec`
    }
    const minutes = convertMsToTime(this.totalTaskCycleTime, "minute")
    if (minutes < 60) {
      return `${Math.floor(minutes)} min`
    }
    const hours = convertMsToTime(this.totalTaskCycleTime, "hour")
    if (hours < 24) {
      return `${Math.floor(hours)} hr`
    }
    const days = convertMsToTime(this.totalTaskCycleTime, "day")
    const numberOfDays = Math.floor(days)
    return numberOfDays === 1 ? `${numberOfDays} day` : `${numberOfDays} days`
  }
}
